import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Collapse,
  Form,
  Input,
  Modal,
  Typography,
  Upload,
} from 'antd';
import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { useCreatePieceMutation } from '../../redux/api/piecesApi';
import { useUploadPieceImageMutation } from '../../redux/api/pieceImagesApi';
import { toast } from 'react-toastify';

const { Text } = Typography;
const { TextArea } = Input;

const EMPTY_FORM = {
  internal_manufacturer_code: '',
  ean_code: '',
  qr_code: '',
  description: '',
};

/**
 * Create a piece. Default UI is minimal (name/code + optional description).
 * Pass simple={false} for the full form (EAN, QR, image).
 */
const PieceCreationModal = ({
  show,
  onHide,
  onPieceCreated,
  productId,
  simple = true,
}) => {
  const [form] = Form.useForm();
  const [createPiece, { isLoading: loading }] = useCreatePieceMutation();
  const [uploadPieceImage] = useUploadPieceImageMutation();
  const [selectedFile, setSelectedFile] = useState(null);
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    if (show) {
      form.setFieldsValue(EMPTY_FORM);
      setSelectedFile(null);
      setShowMore(false);
    }
  }, [show, form]);

  const handleCancel = useCallback(() => {
    form.resetFields();
    setSelectedFile(null);
    setShowMore(false);
    onHide();
  }, [form, onHide]);

  const handleSubmit = async () => {
    if (!productId) {
      toast.error('Select a product first');
      return;
    }

    try {
      // Only name/code is required — validate that field alone.
      await form.validateFields(['internal_manufacturer_code']);
      const values = form.getFieldsValue(true);
      const code = (values.internal_manufacturer_code || '').trim();
      if (!code) {
        form.setFields([
          {
            name: 'internal_manufacturer_code',
            errors: ['Enter a name or manufacturer code'],
          },
        ]);
        return;
      }

      // Use null (not "") for optional unique columns so MySQL UNIQUE allows
      // multiple pieces without EAN/QR.
      const optional = (v) => {
        const trimmed = typeof v === 'string' ? v.trim() : v;
        return trimmed ? trimmed : null;
      };

      const piece = await createPiece({
        internal_manufacturer_code: code,
        ean_code: optional(values.ean_code),
        qr_code: optional(values.qr_code),
        description: optional(values.description),
        product_id: productId,
        is_active: 1,
      }).unwrap();

      if (selectedFile && piece.piece_id) {
        try {
          const imageFormData = new FormData();
          imageFormData.append('image', selectedFile);
          imageFormData.append('description', '');
          imageFormData.append('is_primary', true);
          await uploadPieceImage({
            pieceId: piece.piece_id,
            formData: imageFormData,
          }).unwrap();
        } catch (imageError) {
          console.error('Failed to upload piece image:', imageError);
          toast.warning('Piece created but image upload failed');
        }
      }

      toast.success('Piece created');
      onPieceCreated(piece);
      handleCancel();
    } catch (err) {
      if (err?.errorFields) return;
      toast.error(err?.data?.message || err?.error || 'Failed to create piece');
    }
  };

  const moreFields = (
    <>
      <Form.Item name="ean_code" label="EAN code">
        <Input placeholder="Optional" />
      </Form.Item>
      <Form.Item name="qr_code" label="QR code">
        <Input placeholder="Optional" />
      </Form.Item>
      <Form.Item label="Image">
        <Upload
          beforeUpload={(file) => {
            setSelectedFile(file);
            return false;
          }}
          onRemove={() => setSelectedFile(null)}
          maxCount={1}
          accept="image/*"
          listType="text"
        >
          <Button icon={<UploadOutlined />}>Upload image</Button>
        </Upload>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
          Optional technical drawing or photo
        </Text>
      </Form.Item>
    </>
  );

  return (
    <Modal
      title="Add piece"
      open={!!show}
      onCancel={handleCancel}
      destroyOnHidden
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          icon={<PlusOutlined />}
          loading={loading}
          onClick={handleSubmit}
        >
          Add piece
        </Button>,
      ]}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Give this part a short name or code so you can find it later.
      </Text>
      <Form
        form={form}
        layout="vertical"
        initialValues={EMPTY_FORM}
        requiredMark="optional"
      >
        <Form.Item
          name="internal_manufacturer_code"
          label="Name / code"
          rules={[
            { required: true, message: 'Enter a name or manufacturer code' },
            { whitespace: true, message: 'Enter a name or manufacturer code' },
          ]}
          required
        >
          <Input
            placeholder="e.g. Driver seat, Panel L, SEAT-01"
            autoFocus
            maxLength={120}
            onPressEnter={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          />
        </Form.Item>

        <Form.Item name="description" label="Description" rules={[]}>
          <TextArea
            rows={2}
            placeholder="Optional short description"
            maxLength={500}
            showCount
          />
        </Form.Item>

        {simple ? (
          <Collapse
            ghost
            size="small"
            activeKey={showMore ? ['more'] : []}
            onChange={(keys) => setShowMore(keys.includes('more'))}
            items={[
              {
                key: 'more',
                label: (
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    More options (EAN, QR, image)
                  </Text>
                ),
                children: moreFields,
              },
            ]}
          />
        ) : (
          moreFields
        )}
      </Form>
    </Modal>
  );
};

export default PieceCreationModal;
